import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronLeft, 
  faChevronRight,
  faArrowRight,
  faClock,
  faUsers,
  faCalendarAlt,
  faFilm,
  faPalette,
  faCrown,
  faTree,
  faSeedling,
  faDoorOpen
} from '@fortawesome/free-solid-svg-icons';

const filmTourImage = 'https://BelvoirCastle.b-cdn.net/Tours/film-lovers.webp';
const artTourImage = 'https://BelvoirCastle.b-cdn.net/Tours/art-lovers.webp';
const exclusiveTourImage = 'https://BelvoirCastle.b-cdn.net/Tours/exclusive-tour.webp';
const parklandTourImage = 'https://BelvoirCastle.b-cdn.net/Tours/garden-tour.webp';

// Import tour images

const gardenTourImage = 'https://BelvoirCastle.b-cdn.net/Gardens/rose-garden.webp';

const butlerTourImage = 'https://BelvoirCastle.b-cdn.net/Castle/spiral.webp';

const ToursSliderContainer = styled.section`
  background-color: ${props => props.theme.colors.offWhite};
  padding: 4rem 0;
  position: relative;
  overflow: hidden;
`;

const ToursSliderHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 2rem;
`;

const ToursSliderTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ToursSliderSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 2rem;
`;

const SliderWrapper = styled.div`
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SliderContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
`;

const SliderTrack = styled.div`
  display: flex;
  transition: transform 0.5s ease-in-out;
  transform: translateX(-${props => props.currentSlide * 100}%);
`;

const SlideCard = styled.div`
  min-width: 100%;
  position: relative;
  height: 500px;
  background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.6)), url(${props => props.image});
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: end;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    height: 450px;
  }
  
  @media (max-width: 480px) {
    height: 500px;
  }
`;

const SlideContent = styled.div`
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
  padding: 3rem;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const SlideTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.2rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  
  svg {
    color: ${props => props.theme.colors.accent};
    font-size: 1.8rem;
    flex-shrink: 0;
    margin-top: 0.2rem;
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    gap: 0.75rem;
    line-height: 1.3;
    
    svg {
      font-size: 1.5rem;
      margin-top: 0.1rem;
    }
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
    gap: 0.5rem;
    
    svg {
      font-size: 1.3rem;
    }
  }
`;

const SlideDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  max-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    line-height: 1.4;
  }
`;

const SlideDetails = styled.div`
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const SlideButton = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: ${props => props.theme.colors.accent};
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  text-decoration: none;
  
  &:hover {
    background-color: transparent;
    color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const SliderControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`;

const SliderButton = styled.button`
  background: ${props => props.theme.colors.accent};
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const DotsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Dot = styled.button`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: ${props => props.active ? props.theme.colors.accent : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.colors.accent};
  }
`;

const ViewAllButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: ${props => props.theme.colors.accent};
  text-decoration: none;
  padding: 0.8rem 1.5rem;
  border-radius: 25px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid ${props => props.theme.colors.accent};
  margin-top: 2rem;
  
  &:hover {
    background-color: ${props => props.theme.colors.accent};
    color: white;
  }
  
  svg {
    margin-left: 0.5rem;
  }
`;

const ViewAllContainer = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const ToursSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const tours = [
    {
      title: "What the Butler Saw",
      description: "Join one of our knowledgeable guides on tour taking you to areas of the Castle used by household staff in times gone by. Hear fascinating stories of how the house was run downstairs and on the estate.",
      duration: "2 hours",
      icon: faDoorOpen,
      date: "Selected dates available",
      groupSize: "Limited Availability",
      image: butlerTourImage,
      bookingUrl: "/featured-tours/butler-saw"
    },

    {
      title: "Art Collection Day Tour",
      description: "Every picture tells a story – enjoy a 1 ½ hour tour with our expert guide of the Duke of Rutland's extensive art collection. Discover masterpieces from renowned artists and learn about centuries of collecting.",
      duration: "1 ½ hours",
      icon: faPalette,
      image: artTourImage,
      date: "Selected dates available",
      bookingUrl: "/featured-tours/art-collection"
    },
    {
      title: "Film Lover's Tour",
      description: "Join our knowledgeable guides and step into the State Rooms that were used as locations in the popular Netflix Series, The Crown and films such as The Young Victoria and Victoria & Abdul.",
      duration: "1 ½ hours",
      icon: faFilm,
      image: filmTourImage,
      date: "Selected dates available",
      bookingUrl: "/featured-tours/film-lovers"
    },
    {
      title: "Seasonal Garden Tour",
      description: "Enjoy a seasonal tour with our head gardener and discover the beauty of our Castle Gardens. Enjoy the Snowdrops early February, Daffodils in Spring & Roses all summer long.",
      duration: "1 ½ hours",
      icon: faSeedling,
      image: gardenTourImage,
      date: "Selected dates available",
      bookingUrl: "/featured-tours/garden-tour"
    },
    {
      title: "Parkland Bus Tour",
      description: "Explore the stunning parkland designed by Capability Brown. This guided bus tour takes you through the estate's beautiful landscape, offering unique views and insights into the parkland's history.",
      duration: "1 hour",
      icon: faTree,
      image: parklandTourImage,
      date: "12pm & 3pm (Mon, Thu - Sun)",
      groupSize: "£7 per person",
      bookingUrl: "/tour-booking"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % tours.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + tours.length) % tours.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play functionality
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000); // Change slide every 5 seconds
    return () => clearInterval(timer);
  }, []);

  const handleSlideClick = (tour) => {
    if (tour.bookingUrl) {
      if (tour.bookingUrl.startsWith('http')) {
        window.open(tour.bookingUrl, '_blank', 'noopener noreferrer');
      } else {
        window.location.href = tour.bookingUrl;
      }
    }
  };

  return (
    <ToursSliderContainer>
      <ToursSliderHeader>
        <ToursSliderTitle>Featured Tours</ToursSliderTitle>
        <ToursSliderSubtitle>
          Discover the hidden stories and rich heritage of Belvoir Castle through our expertly guided tours
        </ToursSliderSubtitle>
      </ToursSliderHeader>
      
      <SliderWrapper>
        <SliderContainer>
          <SliderTrack currentSlide={currentSlide}>
            {tours.map((tour, index) => (
              <SlideCard 
                key={index} 
                image={tour.image}
                onClick={() => handleSlideClick(tour)}
              >
                <SlideContent>
                  <SlideTitle>
                    <FontAwesomeIcon icon={tour.icon} />
                    {tour.title}
                  </SlideTitle>
                  <SlideDescription>{tour.description}</SlideDescription>
                  <SlideDetails>
                    {tour.duration && (
                      <DetailItem>
                        <FontAwesomeIcon icon={faClock} />
                        {tour.duration}
                      </DetailItem>
                    )}
                    {tour.date && (
                      <DetailItem>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {tour.date}
                      </DetailItem>
                    )}
                    {tour.groupSize && (
                      <DetailItem>
                        <FontAwesomeIcon icon={faUsers} />
                        {tour.groupSize}
                      </DetailItem>
                    )}
                  </SlideDetails>
                  <SlideButton>
                    {tour.bookingUrl ? (
                      tour.bookingUrl.startsWith('http') ? 'Book Now' : 'Learn More'
                    ) : 'Coming Soon'}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </SlideButton>
                </SlideContent>
              </SlideCard>
            ))}
          </SliderTrack>
        </SliderContainer>
        
        <SliderControls>
          <SliderButton onClick={prevSlide} disabled={tours.length <= 1}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </SliderButton>
          
          <DotsContainer>
            {tours.map((_, index) => (
              <Dot
                key={index}
                active={index === currentSlide}
                onClick={() => goToSlide(index)}
              />
            ))}
          </DotsContainer>
          
          <SliderButton onClick={nextSlide} disabled={tours.length <= 1}>
            <FontAwesomeIcon icon={faChevronRight} />
          </SliderButton>
        </SliderControls>
      </SliderWrapper>
      
      <ViewAllContainer>
        <ViewAllButton to="/whats-on/featured-tours">
          View All Tours <FontAwesomeIcon icon={faArrowRight} />
        </ViewAllButton>
      </ViewAllContainer>
    </ToursSliderContainer>
  );
};

export default ToursSlider; 